import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type {
  DemandAccountRow,
  DemandByAccountReport,
  DemandMonthCell,
  ReportMonth,
  ResourceDemandRow,
  ResourceHoursDirectory,
  ResourceHoursReport,
  ResourceMonthTotal,
} from "@/api/hours-reports";
import { jsonFetch } from "@/lib/json-fetch";
import { hoursReportKeys } from "@/lib/query";

export type {
  DemandAccountRow,
  DemandByAccountReport,
  DemandMonthCell,
  ReportMonth,
  ResourceDemandRow,
  ResourceHoursDirectory,
  ResourceHoursReport,
  ResourceMonthTotal,
};

const STALE_MS = 5 * 60 * 1000;

/** `{ a: "1", b: undefined }` → `?a=1` (undefined and empty values dropped, keys sorted). */
export function toQueryString(params: Record<string, string | number | boolean | undefined>) {
  const qs = new URLSearchParams();
  for (const k of Object.keys(params).sort()) {
    const v = params[k];
    if (v === undefined || v === "") continue;
    qs.set(k, String(v));
  }
  const text = qs.toString();
  return text ? `?${text}` : "";
}

export function useDemandByAccountReport(qs: string) {
  return useQuery<DemandByAccountReport>({
    queryKey: hoursReportKeys.demandByAccount(qs),
    queryFn: () => jsonFetch<DemandByAccountReport>(`/api/reports/demand-by-account${qs}`),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}

/** Who the signed-in user may open on Resource Hours. */
export function useResourceHoursDirectory() {
  return useQuery<ResourceHoursDirectory>({
    queryKey: hoursReportKeys.resourceDirectory(),
    queryFn: () => jsonFetch<ResourceHoursDirectory>("/api/reports/resource-hours"),
    staleTime: STALE_MS,
  });
}

/** One person's hours across a range — powers the Resource Hours page. */
export function useResourceHours(personId: string | null, qs = "") {
  return useQuery<ResourceHoursReport>({
    queryKey: hoursReportKeys.resource(personId ?? "", qs),
    queryFn: () =>
      jsonFetch<ResourceHoursReport>(
        `/api/reports/resource-hours/${encodeURIComponent(personId ?? "")}${qs}`,
      ),
    enabled: personId !== null,
    staleTime: STALE_MS,
  });
}
